import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaArrowLeft, FaTimes, FaSyncAlt } from "react-icons/fa";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

const API_BASE = "https://nissi-india.com/api/ownDetailsEntry";

const OwnVehicleDetailsEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const branchId = Number(
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || 0
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const vehicleId = Number(searchParams.get("vehicleId") || 0);
  const spareId = Number(
    searchParams.get("spareId") || searchParams.get("spareCatId") || 0
  );
  const isEditOwn = vehicleId > 0;

  const [formType, setFormType] = useState(spareId ? "spares" : "own");
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [disableCumFields, setDisableCumFields] = useState(false);

  const [ddl, setDdl] = useState({
    vehicleBranches: [],
    vehicleBasis: [],
    vehicleEquipmentTypes: [],
    vehicleSubTypes: [],
    vehicleConditions: [],
    utilisations: [],
    fuelTypes: [],
    stores: [],
    locations: [],
    vehMainTypes: [],
    sparesItems: [],
    sparesSuppliers: [],
    sparesModels: [],
    sparesMakes: [],
    sparesLocations: [],
  });

  const [own, setOwn] = useState({
    entryDate: "",
    vehicleBranchId: "",
    storeId: "",
    locationId: "",
    jobcardNo: "",
    hypothecationWith: "",
    vehicleTypeId: "",
    vehicleSubTypeId: "",
    vehicleBasisId: "",
    vehicleCode: "",
    vehicleName: "",
    vehicleMake: "",
    vehicleRegNo: "",
    vehicleRegDate: "",
    model: "",
    vehicleUsageCost: "",
    vehicleDescription: "",
    vehicleConditionId: "",
    utilisationId: "",
    remarks: "",
    fuelTypeId: "",
    mainPowerCost: "",
    fuelTankCapacity: "",
    invoiceNo: "",
    chassisNo: "",
    engineSLNo: "",
    equipmentSLNo: "",
    purchasedFrom: "",
    purchasedDate: "",
    purchasedAmount: "",
    insuranceNo: "",
    insuranceDate: "",
    insuranceAmount: "",
    insuranceRenewalDate: "",
    insurancePremiumPerYear: "",
    insuranceCompany: "",
    fcDate: "",
    fcRenewalDate: "",
    roadTaxFromDate: "",
    roadTaxToDate: "",
    permitDate: "",
    permitExpiryDate: "",
    rcBookNo: "",
    localPermitFromDate: "",
    localPermitExpireDate: "",
    trPermitFromDate: "",
    trPermitExpireDate: "",
    freeServiceDate: "",
    averageValue: "",
    averageType: "",
    hubFittingKm: "0",
    hubFittingHr: "0",
    cumHmr: "",
    lastHmr: "",
    cumKms: "",
    lastKms: "",
    adjHmr: "",
    adjKms: "",
  });

  const [docs, setDocs] = useState({
    insurance: null,
    fitness: null,
    roadTax: null,
    permit: null,
    rcCopy: null,
    invoice: null,
    endorsement: null,
  });

  const [spares, setSpares] = useState({
    vehicleMainTypeId: "",
    vehicleCode: "",
    makeId: "",
    makeName: "",
    modelId: "",
    modelName: "",
    itemId: "",
    itemName: "",
    supplierId: "",
    supplierName: "",
    slNo: "",
    purchaseDate: "",
    expiryDate: "",
    purchaseValue: "",
    locationId: "",
    remarks: "",
    uploadDoc: null,
    isNewMake: false,
    isNewModel: false,
    isNewItem: false,
    isNewSupplier: false,
  });

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const toISODateInput = (val) => {
    if (!val) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    const m1 = String(val).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
    return "";
  };

  const getBasisName = (basisId) => {
    const row = (ddl.vehicleBasis || []).find(
      (x) => String(x.id) === String(basisId)
    );
    return String(row?.name || "").toLowerCase();
  };

  const isKmBasis = (basisId) => getBasisName(basisId).includes("km");
  const isHrBasis = (basisId) => {
    const n = getBasisName(basisId);
    return n.includes("hour") || n === "hr" || n.includes("hmr");
  };

  const handleOwnChange = (e) => {
    const { name, value } = e.target;
    setOwn((prev) => ({ ...prev, [name]: value }));
  };

  const handleSparesChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setSpares((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setSpares((prev) => ({ ...prev, [name]: value }));
  };

  const handleDocChange = (key) => (e) => {
    setDocs((prev) => ({ ...prev, [key]: e.target.files?.[0] || null }));
  };

  const handleSparesDocChange = (e) => {
    setSpares((prev) => ({ ...prev, uploadDoc: e.target.files?.[0] || null }));
  };

  const uploadSingle = async (docType, file) => {
    if (!file) return "";
    const fd = new FormData();
    fd.append("docType", docType);
    fd.append("file", file);
    const res = await axios.post(`${API_BASE}/upload`, fd, {
      headers: { ...commonHeaders, "Content-Type": "multipart/form-data" },
    });
    return res.data?.filename || "";
  };

  const loadNextCode = async () => {
    if (isEditOwn) return;
    try {
      setCodeLoading(true);
      const res = await axios.get(`${API_BASE}/nextCode`, {
        headers: commonHeaders,
      });
      const code = res.data?.data?.vehicleCode;
      if (code) setOwn((p) => ({ ...p, vehicleCode: code }));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Could not generate vehicle code");
    } finally {
      setCodeLoading(false);
    }
  };

  const applyMetersByBasis = async (basisId, currentVehicleId = vehicleId) => {
    const km = isKmBasis(basisId);
    const hr = isHrBasis(basisId);

    setOwn((p) => ({
      ...p,
      vehicleBasisId: basisId,
      hubFittingKm: km ? "1" : "0",
      hubFittingHr: hr ? "1" : "0",
      averageType: km ? "KM" : hr ? "HR" : p.averageType,
    }));

    if (!currentVehicleId) return;

    try {
      const res = await axios.get(`${API_BASE}/meters`, {
        params: {
          vehicleId: currentVehicleId,
          basis: km ? "KM" : hr ? "HOUR" : "",
        },
        headers: commonHeaders,
      });
      const d = res.data?.data || {};
      setDisableCumFields(!!d.disableCumFields);
      setOwn((p) => ({
        ...p,
        cumHmr: d.cumHmr ?? p.cumHmr,
        lastHmr: d.lastHmr ?? p.lastHmr,
        cumKms: d.cumKms ?? p.cumKms,
        lastKms: d.lastKms ?? p.lastKms,
        adjHmr: d.adjHmr ?? p.adjHmr,
        adjKms: d.adjKms ?? p.adjKms,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDropdowns = async (branchForStores = branchId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId: branchForStores, branchType, loginUserId },
        headers: commonHeaders,
      });

      const data = res.data || {};
      setDdl({
        vehicleBranches: data.vehicleBranches || [],
        vehicleBasis: data.vehicleBasis || [],
        vehicleEquipmentTypes: data.vehicleEquipmentTypes || [],
        vehicleSubTypes: data.vehicleSubTypes || [],
        vehicleConditions: data.vehicleConditions || [],
        utilisations: data.utilisations || [],
        fuelTypes: data.fuelTypes || [],
        stores: data.stores || [],
        locations: data.locations || [],
        vehMainTypes: data.vehMainTypes || [],
        sparesItems: data.sparesItems || [],
        sparesSuppliers: data.sparesSuppliers || [],
        sparesModels: data.sparesModels || [],
        sparesMakes: data.sparesMakes || [],
        sparesLocations: data.sparesLocations || [],
      });

      setOwn((p) => {
        let next = { ...p };
        if (!next.entryDate) {
          const today = new Date();
          next.entryDate = `${today.getFullYear()}-${String(
            today.getMonth() + 1
          ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        }
        if (branchType !== "HO" && branchId && !next.vehicleBranchId) {
          next.vehicleBranchId = String(branchId);
        }
        return next;
      });

      if (!vehicleId) {
        try {
          const codeRes = await axios.get(`${API_BASE}/nextCode`, {
            headers: commonHeaders,
          });
          const code = codeRes.data?.data?.vehicleCode;
          if (code) setOwn((p) => ({ ...p, vehicleCode: p.vehicleCode || code }));
        } catch (e) {
          console.error(e);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdowns");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubtypesByType = async (vehTypeId) => {
    try {
      if (!vehTypeId) return;
      const res = await axios.get(`${API_BASE}/subtypes`, {
        params: { equipmentTypeId: vehTypeId, branchId, branchType, loginUserId },
        headers: commonHeaders,
      });
      const list = Array.isArray(res.data) ? res.data : res.data?.vehicleSubTypes || [];
      setDdl((p) => ({ ...p, vehicleSubTypes: list || [] }));
    } catch (e) {
      console.error(e);
    }
  };

  const onVehicleTypeChange = async (e) => {
    const v = e.target.value;
    setOwn((p) => ({ ...p, vehicleTypeId: v, vehicleSubTypeId: "" }));
    if (v && v !== "Select") await fetchSubtypesByType(v);
  };

  const fetchStoresByBranch = async (vehBranchId) => {
    try {
      const res = await axios.get(`${API_BASE}/dropdowns`, {
        params: { branchId: vehBranchId, branchType, loginUserId },
        headers: commonHeaders,
      });
      setDdl((p) => ({ ...p, stores: res.data?.stores || [] }));
    } catch (e) {
      console.error(e);
    }
  };

  const generateBaseJobcard = async (branchForJobcard) => {
    try {
      if (!branchForJobcard) return;
      const res = await axios.get(`${API_BASE}/jobcard/base`, {
        params: { branchId: branchForJobcard, loginUserId, branchType },
        headers: commonHeaders,
      });
      const jobcard = res.data?.jobcard || "";
      if (jobcard) setOwn((p) => ({ ...p, jobcardNo: jobcard }));
    } catch (e) {
      console.error(e);
    }
  };

  const onVehicleBranchChange = async (e) => {
    const v = e.target.value;
    setOwn((p) => ({ ...p, vehicleBranchId: v, storeId: "" }));
    if (v) await fetchStoresByBranch(v);
    if (!vehicleId && v) await generateBaseJobcard(v);
  };

  const generateJobcardByVehicle = async () => {
    try {
      if (vehicleId) return;
      if (!own.vehicleBranchId || !own.vehicleCode || !own.vehicleName) return;
      const res = await axios.get(`${API_BASE}/jobcard/by-vehicle`, {
        params: {
          selectedBranchId: own.vehicleBranchId,
          vehicleCode: own.vehicleCode,
          vehicleName: own.vehicleName,
          loginUserId,
          branchType,
          branchId,
        },
        headers: commonHeaders,
      });
      const jobcard = res.data?.jobcard || "";
      if (jobcard) setOwn((p) => ({ ...p, jobcardNo: jobcard }));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchOwnDetailsForEdit = async (id) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/view`, {
        params: { vehicleId: id, branchId, branchType, loginUserId },
        headers: commonHeaders,
      });
      const d = res.data?.vehicle || null;
      if (!d) {
        toast.warn("No vehicle data found");
        return;
      }

      setDisableCumFields(!!res.data?.disableCumFields);
      setFormType("own");

      const basisId = String(d.fld_vehicle_basis ?? d.vehicleBasisId ?? "");

      setOwn((p) => ({
        ...p,
        entryDate: toISODateInput(d.fld_entrydate || d.entryDate),
        vehicleBranchId: String(d.fld_branchid ?? d.vehicleBranchId ?? ""),
        storeId: String(d.fld_storeid ?? d.storeId ?? ""),
        locationId: String(d.fld_locationid ?? d.locationId ?? ""),
        jobcardNo: d.fld_jobcardno ?? d.jobcardNo ?? "",
        hypothecationWith: d.fld_Hypothecat_with ?? d.hypothecationWith ?? "",
        vehicleTypeId: String(d.fld_vehicletypeid ?? d.vehicleTypeId ?? ""),
        vehicleSubTypeId: String(d.fld_vehicle_subtypeid ?? d.vehicleSubTypeId ?? ""),
        vehicleBasisId: basisId,
        vehicleCode: d.fld_vehicle_code ?? d.vehicleCode ?? "",
        vehicleName: d.fld_vehicle_name ?? d.vehicleName ?? "",
        vehicleMake: d.fld_vehiclemake ?? d.vehicleMake ?? "",
        vehicleRegNo: d.fld_vehicle_Regno ?? d.vehicleRegNo ?? "",
        vehicleRegDate: toISODateInput(d.fld_vehiclereg_date ?? d.vehicleRegDate),
        model: d.fld_vehicle_model ?? d.model ?? "",
        vehicleUsageCost: d.fld_usage_cost ?? d.vehicleUsageCost ?? "",
        vehicleDescription: d.fld_vehicle_description ?? d.vehicleDescription ?? "",
        vehicleConditionId: String(d.fld_vehicle_condition ?? d.vehicleConditionId ?? ""),
        utilisationId: String(d.fld_utilisation ?? d.utilisationId ?? ""),
        remarks: d.fld_remarks ?? d.remarks ?? "",
        fuelTypeId: String(d.fld_fueltypeid ?? d.fuelTypeId ?? ""),
        mainPowerCost: d.fld_powercost ?? d.mainPowerCost ?? "",
        fuelTankCapacity: d.fld_vehfueltankcapacity ?? d.fuelTankCapacity ?? "",
        invoiceNo: d.fld_invoiceno ?? d.invoiceNo ?? "",
        chassisNo: d.fld_Chassisno ?? d.chassisNo ?? "",
        engineSLNo: d.fld_engineSLNO ?? d.engineSLNo ?? "",
        equipmentSLNo: d.fld_EquipmentSLNO ?? d.equipmentSLNo ?? "",
        purchasedFrom: d.fld_purchasedfrom ?? d.purchasedFrom ?? "",
        purchasedDate: toISODateInput(d.fld_purchaseddate ?? d.purchasedDate),
        purchasedAmount: d.fld_purchased_amt ?? d.purchasedAmount ?? "",
        insuranceNo: d.fld_insuranceno ?? d.insuranceNo ?? "",
        insuranceDate: toISODateInput(d.fld_insurance_date ?? d.insuranceDate),
        insuranceAmount: d.fld_insurance_amt ?? d.insuranceAmount ?? "",
        insuranceRenewalDate: toISODateInput(
          d.fld_insurance_renewal_date ?? d.insuranceRenewalDate
        ),
        insurancePremiumPerYear:
          d.fld_insurancepreminum_peryear ?? d.insurancePremiumPerYear ?? "",
        insuranceCompany: d.fld_insurance_company ?? d.insuranceCompany ?? "",
        fcDate: toISODateInput(d.fld_fcdate ?? d.fcDate),
        fcRenewalDate: toISODateInput(d.fld_fc_renewal_date ?? d.fcRenewalDate),
        roadTaxFromDate: toISODateInput(d.fld_roadtaxfrom_date ?? d.roadTaxFromDate),
        roadTaxToDate: toISODateInput(d.fld_roadtaxto_date ?? d.roadTaxToDate),
        permitDate: toISODateInput(d.fld_permit_date ?? d.permitDate),
        permitExpiryDate: toISODateInput(d.fld_pemitexpire_date ?? d.permitExpiryDate),
        rcBookNo: d.fld_rc_bookno ?? d.rcBookNo ?? "",
        localPermitFromDate: toISODateInput(d.fld_localpermit_date ?? d.localPermitFromDate),
        localPermitExpireDate: toISODateInput(
          d.fld_localpermitexpire_date ?? d.localPermitExpireDate
        ),
        trPermitFromDate: toISODateInput(d.fld_TRpermit_date ?? d.trPermitFromDate),
        trPermitExpireDate: toISODateInput(
          d.fld_TRpermitexpire_date ?? d.trPermitExpireDate
        ),
        freeServiceDate: toISODateInput(d.fld_freeservicedate ?? d.freeServiceDate),
        averageValue: d.fld_average ?? d.averageValue ?? "",
        averageType: d.fld_averagekmhr ?? d.averageType ?? "",
        hubFittingKm: String(d.rd_hsdfillingkm ?? d.fld_hsdfillingkm ?? d.hubFittingKm ?? "0"),
        hubFittingHr: String(d.rd_hsdfillinghr ?? d.fld_hsdfillinghr ?? d.hubFittingHr ?? "0"),
        cumHmr: d.fld_cumHMR ?? d.cumHmr ?? "",
        lastHmr: d.fld_lastupdatedHMR ?? d.lastHmr ?? "",
        cumKms: d.fld_cumKMR ?? d.cumKms ?? "",
        lastKms: d.fld_lastupdatedKMR ?? d.lastKms ?? "",
        adjHmr: d.fld_adjustmentHMR ?? d.adjHmr ?? "",
        adjKms: d.fld_adjustmentKMR ?? d.adjKms ?? "",
      }));

      const selectedType = String(d.fld_vehicletypeid ?? "");
      if (selectedType) await fetchSubtypesByType(selectedType);
      const b = String(d.fld_branchid ?? "");
      if (b) await fetchStoresByBranch(b);
      if (basisId) await applyMetersByBasis(basisId, id);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load vehicle details");
    } finally {
      setLoading(false);
    }
  };

  const fetchSparesForEdit = async (id) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/spares/view`, {
        params: { spareCatId: id, branchId, branchType, loginUserId },
        headers: commonHeaders,
      });
      const d = res.data?.spares || null;
      if (!d) {
        toast.warn("No spares data found");
        return;
      }
      setFormType("spares");
      setSpares((p) => ({
        ...p,
        vehicleMainTypeId: String(d.fld_vehiclemaintypeid ?? ""),
        vehicleCode: d.fld_sparesvehiclecode ?? "",
        makeId: "",
        makeName: d.fld_make ?? "",
        modelId: "",
        modelName: d.fld_model ?? "",
        itemId: String(d.fld_sparesitemid ?? ""),
        itemName: "",
        supplierId: String(d.fld_sparessupplierid ?? ""),
        supplierName: "",
        slNo: d.fld_slno ?? "",
        purchaseDate: toISODateInput(d.fld_Date ?? d.fld_purchasedate),
        expiryDate: toISODateInput(d.fld_expDate ?? d.fld_expirydate),
        purchaseValue: d.fld_purchasevalue ?? "",
        locationId: String(d.fld_location ?? ""),
        remarks: d.fld_remarks ?? "",
        uploadDoc: null,
        isNewMake: !!d.fld_make,
        isNewModel: !!d.fld_model,
        isNewItem: false,
        isNewSupplier: false,
      }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load spares details");
    } finally {
      setLoading(false);
    }
  };

  const onVehicleBasisChange = async (e) => {
    await applyMetersByBasis(e.target.value, vehicleId);
  };

  const handleSubmit = async () => {
    if (loading) return;

    try {
      setLoading(true);

      if (formType === "own") {
        if (!own.vehicleTypeId) return toast.error("Select Vehicle Type");
        if (!own.vehicleCode) return toast.error("Enter Vehicle Code");
        if (!own.vehicleName) return toast.error("Enter Vehicle Name");
        if (!own.vehicleBasisId) return toast.error("Select Vehicle Basis");
        if (!own.fuelTypeId) return toast.error("Select Fuel Type");
        if (!own.locationId) return toast.error("Select Location");

        const uploaded = {
          insurance: [],
          fitness: [],
          roadtax: [],
          permit: [],
          rccopy: [],
          invoice: [],
          emission: [],
        };

        const insuranceFn = await uploadSingle("InsuranceDocument", docs.insurance);
        if (insuranceFn) uploaded.insurance.push(insuranceFn);
        const fitnessFn = await uploadSingle("FitnessDocument", docs.fitness);
        if (fitnessFn) uploaded.fitness.push(fitnessFn);
        const roadTaxFn = await uploadSingle("RoadTaxDocument", docs.roadTax);
        if (roadTaxFn) uploaded.roadtax.push(roadTaxFn);
        const permitFn = await uploadSingle("PermitDocument", docs.permit);
        if (permitFn) uploaded.permit.push(permitFn);
        const rcCopyFn = await uploadSingle("RcCopyDocument", docs.rcCopy);
        if (rcCopyFn) uploaded.rccopy.push(rcCopyFn);
        const invoiceFn = await uploadSingle("InvoiceDocument", docs.invoice);
        if (invoiceFn) uploaded.invoice.push(invoiceFn);

        const payload = {
          mode: vehicleId ? "update" : "insert",
          vehicleId,
          branchId,
          branchType,
          loginUserId,
          ipAddress: "",
          entryDate: own.entryDate,
          vehicleBranchId: own.vehicleBranchId || branchId,
          storeId: own.storeId || 0,
          locationId: own.locationId,
          jobcardNo: own.jobcardNo || "",
          vehicleEquipmentTypeId: own.vehicleTypeId,
          vehicleSubTypeId: own.vehicleSubTypeId || "Select",
          vehicleBasisId: own.vehicleBasisId,
          vehicleBasisText: "",
          vehicleCode: own.vehicleCode,
          vehicleName: own.vehicleName,
          vehicleMake: own.vehicleMake,
          vehicleRegNo: own.vehicleRegNo,
          vehicleRegDate: own.vehicleRegDate,
          model: own.model,
          vehicleDesc: own.vehicleDescription,
          vehicleConditionId: own.vehicleConditionId || 0,
          utilisationId: own.utilisationId || 0,
          remarks: own.remarks || "",
          fuelTypeId: own.fuelTypeId,
          usageCost: own.vehicleUsageCost || 0,
          manpowerCost: own.mainPowerCost || 0,
          powerCost: own.mainPowerCost || 0,
          fuelTankCapacity: own.fuelTankCapacity || "",
          invoiceNo: own.invoiceNo || "",
          chassisNo: own.chassisNo || "",
          engineSlNo: own.engineSLNo || "",
          equipmentSlNo: own.equipmentSLNo || "",
          purchasedFrom: own.purchasedFrom || "",
          purchasedDate: own.purchasedDate || "",
          purchasedAmt: own.purchasedAmount || 0,
          hypothecation: own.hypothecationWith || "",
          insuranceNo: own.insuranceNo || "",
          insuranceDate: own.insuranceDate || "",
          insuranceAmt: own.insuranceAmount || 0,
          insuranceRenewalDate: own.insuranceRenewalDate || "",
          insurancePremiumPerYear: own.insurancePremiumPerYear || "",
          insuranceCompany: own.insuranceCompany || "",
          fcDate: own.fcDate || "",
          fcRenewalDate: own.fcRenewalDate || "",
          roadTaxFromDate: own.roadTaxFromDate || "",
          roadTaxToDate: own.roadTaxToDate || "",
          permitDate: own.permitDate || "",
          permitExpDate: own.permitExpiryDate || "",
          localPermitDate: own.localPermitFromDate || "",
          localPermitExpDate: own.localPermitExpireDate || "",
          trPermitDate: own.trPermitFromDate || "",
          trPermitExpDate: own.trPermitExpireDate || "",
          rcBookNo: own.rcBookNo || "",
          freeServiceDate: own.freeServiceDate || "",
          average: own.averageValue || "",
          averageUnit: own.averageType === "HR" ? "HR" : "KM",
          hsdKm: own.hubFittingKm === "1" ? 1 : 0,
          hsdHr: own.hubFittingHr === "1" ? 1 : 0,
          cumulativeHMR: own.cumHmr || "0",
          lastHMR: own.lastHmr || "0",
          cumulativeKMR: own.cumKms || "0",
          lastKMR: own.lastKms || "0",
          adjustHMR: own.adjHmr || "0",
          adjustKMR: own.adjKms || "0",
          docs: uploaded,
        };

        const res = await axios.post(`${API_BASE}/save`, payload, {
          headers: commonHeaders,
        });
        toast.success(`Own Vehicle ${res.data?.mode || ""} saved successfully`);
        if (!vehicleId && res.data?.vehicleId) {
          navigate(`/vehicle/OwnVehicle_DetailsEntry?vehicleId=${res.data.vehicleId}`);
        }
      }

      if (formType === "spares") {
        if (!spares.vehicleMainTypeId) return toast.error("Select Vehicle Main Type");
        if (!spares.vehicleCode) return toast.error("Enter Vehicle Code");
        if (!spares.purchaseDate) return toast.error("Select Purchase Date");

        const sparesDocName = await uploadSingle("SparesDocument", spares.uploadDoc);

        const sparesPayload = {
          branchId,
          branchType,
          loginUserId,
          spareCatId: spareId ? String(spareId) : "",
          vehMainTypeId: spares.vehicleMainTypeId,
          vehicleCode: spares.vehicleCode,
          itemId: spares.isNewItem ? 0 : spares.itemId,
          itemNewName: spares.isNewItem ? spares.itemName : "",
          supplierId: spares.isNewSupplier ? 0 : spares.supplierId,
          supplierNewName: spares.isNewSupplier ? spares.supplierName : "",
          makeId: spares.isNewMake ? 0 : spares.makeId,
          makeNewName: spares.isNewMake ? spares.makeName : "",
          makeName: spares.isNewMake ? spares.makeName : "",
          modelId: spares.isNewModel ? 0 : spares.modelId,
          modelNewName: spares.isNewModel ? spares.modelName : "",
          modelName: spares.isNewModel ? spares.modelName : "",
          slno: spares.slNo || "",
          purchaseDate: spares.purchaseDate,
          expiryDate: spares.expiryDate,
          purchaseValue: spares.purchaseValue || "",
          locationId: spares.locationId || 0,
          remarks: spares.remarks || "",
          documentFilename: sparesDocName || "",
        };

        const res = await axios.post(`${API_BASE}/spares/save`, sparesPayload, {
          headers: commonHeaders,
        });
        toast.success(`Spares ${res.data?.mode || ""} saved successfully`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (formType === "own") {
      setOwn((p) => ({
        ...p,
        jobcardNo: "",
        hypothecationWith: "",
        vehicleTypeId: "",
        vehicleSubTypeId: "",
        vehicleBasisId: "",
        vehicleCode: "",
        vehicleName: "",
        vehicleMake: "",
        vehicleRegNo: "",
        vehicleRegDate: "",
        model: "",
        vehicleUsageCost: "",
        vehicleDescription: "",
        vehicleConditionId: "",
        utilisationId: "",
        remarks: "",
        fuelTypeId: "",
        mainPowerCost: "",
        fuelTankCapacity: "",
        invoiceNo: "",
        chassisNo: "",
        engineSLNo: "",
        equipmentSLNo: "",
        purchasedFrom: "",
        purchasedDate: "",
        purchasedAmount: "",
        insuranceNo: "",
        insuranceDate: "",
        insuranceAmount: "",
        insuranceRenewalDate: "",
        insurancePremiumPerYear: "",
        insuranceCompany: "",
        fcDate: "",
        fcRenewalDate: "",
        roadTaxFromDate: "",
        roadTaxToDate: "",
        permitDate: "",
        permitExpiryDate: "",
        rcBookNo: "",
        localPermitFromDate: "",
        localPermitExpireDate: "",
        trPermitFromDate: "",
        trPermitExpireDate: "",
        freeServiceDate: "",
        averageValue: "",
        averageType: "",
        hubFittingKm: "0",
        hubFittingHr: "0",
        cumHmr: "",
        lastHmr: "",
        cumKms: "",
        lastKms: "",
        adjHmr: "",
        adjKms: "",
      }));
      setDocs({
        insurance: null,
        fitness: null,
        roadTax: null,
        permit: null,
        rcCopy: null,
        invoice: null,
        endorsement: null,
      });
      generateBaseJobcard(own.vehicleBranchId || branchId);
      if (!vehicleId) setTimeout(() => loadNextCode(), 0);
    } else {
      setSpares({
        vehicleMainTypeId: "",
        vehicleCode: "",
        makeId: "",
        makeName: "",
        modelId: "",
        modelName: "",
        itemId: "",
        itemName: "",
        supplierId: "",
        supplierName: "",
        slNo: "",
        purchaseDate: "",
        expiryDate: "",
        purchaseValue: "",
        locationId: "",
        remarks: "",
        uploadDoc: null,
        isNewMake: false,
        isNewModel: false,
        isNewItem: false,
        isNewSupplier: false,
      });
    }
    toast.info("Form cleared!");
  };

  useEffect(() => {
    fetchDropdowns(branchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!vehicleId && !spareId) return;
    if (vehicleId) fetchOwnDetailsForEdit(vehicleId);
    if (spareId) fetchSparesForEdit(spareId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, spareId]);

  useEffect(() => {
    if (vehicleId) return;
    const b = own.vehicleBranchId || branchId;
    if (!own.jobcardNo && b) generateBaseJobcard(b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, own.vehicleBranchId]);

  useEffect(() => {
    generateJobcardByVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [own.vehicleCode, own.vehicleName, own.vehicleBranchId]);

  const showKmMeters = isKmBasis(own.vehicleBasisId);
  const showHrMeters = isHrBasis(own.vehicleBasisId);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE DETAILS ENTRY</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                {formType === "own"
                  ? `Own Vehicle ${isEditOwn ? "Edit" : "Entry"}`
                  : "Spares And Catalogue"}
              </h2>
              {formType === "own" && !isEditOwn && (
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Vehicle code is generated automatically
                </p>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate(-1)}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {loading ? "Saving..." : isEditOwn || spareId ? "Update" : "Save"}
              </button>
            </div>
          </div>

          <Section title="Entry Type" isDark={isDark}>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="formType"
                  value="own"
                  checked={formType === "own"}
                  onChange={() => setFormType("own")}
                  className="w-4 h-4 text-blue-600"
                />
                <span>Own Vehicle</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="formType"
                  value="spares"
                  checked={formType === "spares"}
                  onChange={() => setFormType("spares")}
                  className="w-4 h-4 text-blue-600"
                />
                <span>Spares And Catalogue</span>
              </label>
            </div>
          </Section>

          {formType === "own" && (
            <>
              <Section title="Own Vehicle Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <FieldInput label="Entry Date" type="date" name="entryDate" value={own.entryDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldSelect label="Vehicle Branch" name="vehicleBranchId" value={own.vehicleBranchId} onChange={onVehicleBranchChange} options={ddl.vehicleBranches} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Jobcard No" name="jobcardNo" value={own.jobcardNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Hypothecation With" name="hypothecationWith" value={own.hypothecationWith} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Vehicle General Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <FieldSelect label="Vehicle Type *" name="vehicleTypeId" value={own.vehicleTypeId} onChange={onVehicleTypeChange} options={ddl.vehicleEquipmentTypes} inputClass={inputClass} labelClass={labelClass} />
                  <FieldSelect label="Vehicle Subtype" name="vehicleSubTypeId" value={own.vehicleSubTypeId} onChange={handleOwnChange} options={ddl.vehicleSubTypes} inputClass={inputClass} labelClass={labelClass} />

                  <div>
                    <label className={labelClass}>Vehicle Code *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="vehicleCode"
                        value={own.vehicleCode}
                        onChange={handleOwnChange}
                        readOnly={!isEditOwn}
                        className={`${inputClass} ${!isEditOwn ? "opacity-90 cursor-not-allowed" : ""}`}
                      />
                      {!isEditOwn && (
                        <button
                          type="button"
                          onClick={loadNextCode}
                          disabled={codeLoading}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap flex items-center gap-2 ${
                            isDark
                              ? "bg-gray-700 border border-gray-600 text-white"
                              : "bg-white border border-gray-300 text-gray-700"
                          }`}
                        >
                          <FaSyncAlt className={codeLoading ? "animate-spin" : ""} />
                          {codeLoading ? "..." : "Generate"}
                        </button>
                      )}
                    </div>
                  </div>

                  <FieldInput label="Vehicle Name *" name="vehicleName" value={own.vehicleName} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Vehicle Make" name="vehicleMake" value={own.vehicleMake} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Vehicle Reg No" name="vehicleRegNo" value={own.vehicleRegNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Vehicle Usage Cost" name="vehicleUsageCost" value={own.vehicleUsageCost} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Vehicle Reg Date" type="date" name="vehicleRegDate" value={own.vehicleRegDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Model" name="model" value={own.model} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />

                  <FieldSelect
                    label="Vehicle Basis *"
                    name="vehicleBasisId"
                    value={own.vehicleBasisId}
                    onChange={onVehicleBasisChange}
                    options={ddl.vehicleBasis}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  {showHrMeters && (
                    <>
                      <FieldInput label="Current Cumulative HMR" name="cumHmr" value={own.cumHmr} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} readOnly={disableCumFields} />
                      <FieldInput label="Last Updated HMR" name="lastHmr" value={own.lastHmr} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} readOnly={disableCumFields} />
                      <FieldInput label="Adjustment HMR" name="adjHmr" value={own.adjHmr} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                    </>
                  )}

                  {showKmMeters && (
                    <>
                      <FieldInput label="Current Cumulative KMS" name="cumKms" value={own.cumKms} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} readOnly={disableCumFields} />
                      <FieldInput label="Last Updated KMS" name="lastKms" value={own.lastKms} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} readOnly={disableCumFields} />
                      <FieldInput label="Adjustment KMS" name="adjKms" value={own.adjKms} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                    </>
                  )}

                  <FieldSelect label="Store" name="storeId" value={own.storeId} onChange={handleOwnChange} options={ddl.stores} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Main Power Cost" name="mainPowerCost" value={own.mainPowerCost} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldSelect label="Location *" name="locationId" value={own.locationId} onChange={handleOwnChange} options={ddl.locations} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Vehicle Description" name="vehicleDescription" value={own.vehicleDescription} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldSelect label="Vehicle Condition" name="vehicleConditionId" value={own.vehicleConditionId} onChange={handleOwnChange} options={ddl.vehicleConditions} inputClass={inputClass} labelClass={labelClass} />
                  <FieldSelect label="Utilisation" name="utilisationId" value={own.utilisationId} onChange={handleOwnChange} options={ddl.utilisations} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Insurance Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <FieldInput label="Insurance Number" name="insuranceNo" value={own.insuranceNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Insurance Date" type="date" name="insuranceDate" value={own.insuranceDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Insurance Amount" name="insuranceAmount" value={own.insuranceAmount} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Insurance Renewal Date" type="date" name="insuranceRenewalDate" value={own.insuranceRenewalDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Insurance Premium Per Year" name="insurancePremiumPerYear" value={own.insurancePremiumPerYear} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Insurance Company" name="insuranceCompany" value={own.insuranceCompany} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Vehicle Parts Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <FieldInput label="Coach / Chassis Number" name="chassisNo" value={own.chassisNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Engine SL No" name="engineSLNo" value={own.engineSLNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="First Service Date" type="date" name="freeServiceDate" value={own.freeServiceDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldSelect label="Fuel Type *" name="fuelTypeId" value={own.fuelTypeId} onChange={handleOwnChange} options={ddl.fuelTypes} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Equipment S/No" name="equipmentSLNo" value={own.equipmentSLNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Fuel Tank Capacity" name="fuelTankCapacity" value={own.fuelTankCapacity} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="FC Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FieldInput label="FC Date" type="date" name="fcDate" value={own.fcDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="FC Renewal Date" type="date" name="fcRenewalDate" value={own.fcRenewalDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Purchase Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <FieldInput label="Purchased From" name="purchasedFrom" value={own.purchasedFrom} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Purchased Date" type="date" name="purchasedDate" value={own.purchasedDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Purchased Amount" name="purchasedAmount" value={own.purchasedAmount} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Invoice Number" name="invoiceNo" value={own.invoiceNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />

                  <RadioYesNo
                    label="Hub Filling KM"
                    value={own.hubFittingKm === "1"}
                    onYes={() => setOwn((p) => ({ ...p, hubFittingKm: "1" }))}
                    onNo={() => setOwn((p) => ({ ...p, hubFittingKm: "0" }))}
                    isDark={isDark}
                    hint="Diesel tracked by odometer"
                  />
                  <RadioYesNo
                    label="Hub Filling HR"
                    value={own.hubFittingHr === "1"}
                    onYes={() => setOwn((p) => ({ ...p, hubFittingHr: "1" }))}
                    onNo={() => setOwn((p) => ({ ...p, hubFittingHr: "0" }))}
                    isDark={isDark}
                    hint="Diesel tracked by hour meter"
                  />

                  <div>
                    <label className={labelClass}>Average Type</label>
                    <div className="flex gap-6 pt-1">
                      {["KM", "HR"].map((t) => (
                        <label key={t} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={own.averageType === t}
                            onChange={() => setOwn((p) => ({ ...p, averageType: t }))}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span>{t === "HR" ? "HMR" : "KM"}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <FieldInput label="Average Value" name="averageValue" value={own.averageValue} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Tax Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <FieldInput label="Road Tax From Date" type="date" name="roadTaxFromDate" value={own.roadTaxFromDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Road Tax To Date" type="date" name="roadTaxToDate" value={own.roadTaxToDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Permit Date" type="date" name="permitDate" value={own.permitDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="RC Book Number" name="rcBookNo" value={own.rcBookNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Permit Expiry Date" type="date" name="permitExpiryDate" value={own.permitExpiryDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Local Permit From Date" type="date" name="localPermitFromDate" value={own.localPermitFromDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="Local Permit Expire Date" type="date" name="localPermitExpireDate" value={own.localPermitExpireDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="TR Permit From Date" type="date" name="trPermitFromDate" value={own.trPermitFromDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <FieldInput label="TR Permit Expire Date" type="date" name="trPermitExpireDate" value={own.trPermitExpireDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Scanned Documents" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FileInput label="Insurance Document" onChange={handleDocChange("insurance")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Fitness Document" onChange={handleDocChange("fitness")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Road Tax Document" onChange={handleDocChange("roadTax")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Permit Document" onChange={handleDocChange("permit")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="RC Copy Document" onChange={handleDocChange("rcCopy")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Invoice Document" onChange={handleDocChange("invoice")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Endorsement Document" onChange={handleDocChange("endorsement")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                </div>
              </Section>
            </>
          )}

          {formType === "spares" && (
            <Section title="Spares And Catalogue" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <FieldSelect label="Vehicle Main Type *" name="vehicleMainTypeId" value={spares.vehicleMainTypeId} onChange={handleSparesChange} options={ddl.vehMainTypes} inputClass={inputClass} labelClass={labelClass} />
                <FieldInput label="Vehicle Code *" name="vehicleCode" value={spares.vehicleCode} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />

                <NewableField
                  label="Make"
                  isNew={spares.isNewMake}
                  nameId="makeId"
                  nameText="makeName"
                  valueId={spares.makeId}
                  valueText={spares.makeName}
                  newFlag="isNewMake"
                  options={ddl.sparesMakes}
                  onChange={handleSparesChange}
                  inputClass={inputClass}
                  labelClass={labelClass}
                  isDark={isDark}
                />
                <NewableField
                  label="Model"
                  isNew={spares.isNewModel}
                  nameId="modelId"
                  nameText="modelName"
                  valueId={spares.modelId}
                  valueText={spares.modelName}
                  newFlag="isNewModel"
                  options={ddl.sparesModels}
                  onChange={handleSparesChange}
                  inputClass={inputClass}
                  labelClass={labelClass}
                  isDark={isDark}
                />
                <NewableField
                  label="Description"
                  isNew={spares.isNewItem}
                  nameId="itemId"
                  nameText="itemName"
                  valueId={spares.itemId}
                  valueText={spares.itemName}
                  newFlag="isNewItem"
                  options={ddl.sparesItems}
                  onChange={handleSparesChange}
                  inputClass={inputClass}
                  labelClass={labelClass}
                  isDark={isDark}
                />

                <FieldInput label="SL. NO" name="slNo" value={spares.slNo} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                <FieldInput label="Purchase Date *" type="date" name="purchaseDate" value={spares.purchaseDate} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                <FieldInput label="Expiry Date" type="date" name="expiryDate" value={spares.expiryDate} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                <FieldInput label="Purchase Value" name="purchaseValue" value={spares.purchaseValue} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />

                <NewableField
                  label="Purchased From"
                  isNew={spares.isNewSupplier}
                  nameId="supplierId"
                  nameText="supplierName"
                  valueId={spares.supplierId}
                  valueText={spares.supplierName}
                  newFlag="isNewSupplier"
                  options={ddl.sparesSuppliers}
                  onChange={handleSparesChange}
                  inputClass={inputClass}
                  labelClass={labelClass}
                  isDark={isDark}
                />

                <FieldSelect label="Location" name="locationId" value={spares.locationId} onChange={handleSparesChange} options={ddl.sparesLocations} inputClass={inputClass} labelClass={labelClass} />
                <FieldInput label="Remarks" name="remarks" value={spares.remarks} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                <FileInput label="Upload Document" onChange={handleSparesDocChange} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
              </div>
            </Section>
          )}

          <div className="flex justify-between items-center mt-2 pt-6 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
            <button
              onClick={handleClear}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaTimes />
              Clear
            </button>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(-1)}
                disabled={loading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {loading ? "Please wait..." : "Submit"}
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function FieldInput({
  label,
  type = "text",
  name,
  value,
  onChange,
  inputClass,
  labelClass,
  readOnly,
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        readOnly={!!readOnly}
        className={`${inputClass} ${readOnly ? "opacity-80 cursor-not-allowed" : ""}`}
      />
    </div>
  );
}

function FieldSelect({ label, name, value, onChange, options, inputClass, labelClass }) {
  const isObjectOptions =
    Array.isArray(options) && options.length > 0 && typeof options[0] === "object";

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select name={name} value={value ?? ""} onChange={onChange} className={inputClass}>
        <option value="">Select</option>
        {isObjectOptions
          ? options.map((opt) => (
              <option key={opt.id} value={String(opt.id)}>
                {opt.name}
              </option>
            ))
          : (options || []).map((opt, idx) => (
              <option key={idx} value={String(opt)}>
                {opt}
              </option>
            ))}
      </select>
    </div>
  );
}

function FileInput({ label, onChange, inputClass, labelClass, isDark }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input
        type="file"
        onChange={onChange}
        className={`${inputClass} file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 ${
          isDark ? "file:bg-gray-700 file:text-white" : "file:bg-gray-200 file:text-gray-700"
        } file:text-sm file:font-semibold`}
      />
    </div>
  );
}

function RadioYesNo({ label, value, onYes, onNo, isDark, hint }) {
  return (
    <div>
      <label className={`block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {label}
      </label>
      <div className="flex gap-6 pt-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" checked={!!value} onChange={onYes} className="w-4 h-4 text-blue-600" />
          <span>Yes</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" checked={!value} onChange={onNo} className="w-4 h-4 text-blue-600" />
          <span>No</span>
        </label>
      </div>
      {hint ? (
        <p className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>{hint}</p>
      ) : null}
    </div>
  );
}

function NewableField({
  label,
  isNew,
  nameId,
  nameText,
  valueId,
  valueText,
  newFlag,
  options,
  onChange,
  inputClass,
  labelClass,
  isDark,
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1">
        {isNew ? (
          <FieldInput
            label={label}
            name={nameText}
            value={valueText}
            onChange={onChange}
            inputClass={inputClass}
            labelClass={labelClass}
          />
        ) : (
          <FieldSelect
            label={label}
            name={nameId}
            value={valueId}
            onChange={onChange}
            options={options}
            inputClass={inputClass}
            labelClass={labelClass}
          />
        )}
      </div>
      <label className="flex items-center gap-1 mt-8 cursor-pointer whitespace-nowrap">
        <input
          type="checkbox"
          name={newFlag}
          checked={isNew}
          onChange={onChange}
          className="text-blue-600"
        />
        <span className={isDark ? "text-gray-300" : "text-gray-700"}>New</span>
      </label>
    </div>
  );
}

export default OwnVehicleDetailsEntry;